import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { Inbox, Mail } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  type ReceivedInvoicePipelineStatus,
  useReceivedInvoicesList,
} from "../api/receivedInvoicesApi";
import { MailboxSetupDialog } from "../components/MailboxSetupDialog";
import {
  ReceivedInvoicesFilters,
  type ReceivedInvoicesUiFilters,
} from "../components/ReceivedInvoicesFilters";
import { ReceivedInvoicesList } from "../components/ReceivedInvoicesList";

const VALID_STATUSES: ReceivedInvoicePipelineStatus[] = [
  "received",
  "ready",
  "partially_assigned",
  "assigned",
  "quarantined",
  "dismissed",
];

function parseStatusParam(raw: string | null): ReceivedInvoicePipelineStatus | "" {
  if (!raw) return "";
  return (VALID_STATUSES as readonly string[]).includes(raw)
    ? (raw as ReceivedInvoicePipelineStatus)
    : "";
}

function parseDateParam(raw: string | null): string {
  return raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : "";
}

export function RecibidosListPage() {
  const { currentAccount, currentMembership } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const role = currentMembership?.role;
  const canManage = role === "owner" || role === "admin";

  const [searchParams, setSearchParams] = useSearchParams();
  const filters: ReceivedInvoicesUiFilters = {
    status: parseStatusParam(searchParams.get("status")),
    from: parseDateParam(searchParams.get("from")),
    to: parseDateParam(searchParams.get("to")),
  };

  function setFilters(next: ReceivedInvoicesUiFilters) {
    const params = new URLSearchParams();
    if (next.status) params.set("status", next.status);
    if (next.from) params.set("from", next.from);
    if (next.to) params.set("to", next.to);
    setSearchParams(params, { replace: true });
  }

  const invoicesQuery = useReceivedInvoicesList(accountId, {
    status: filters.status || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  });

  const [mailboxOpen, setMailboxOpen] = useState(false);

  const invoices = invoicesQuery.data?.invoices ?? [];
  const hasFilters = filters.status !== "" || filters.from !== "" || filters.to !== "";

  return (
    <>
      <title>Recibidos · HATO</title>

      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-medium text-3xl text-app-ink-1 tracking-tight">Recibidos</h1>
          <p className="mt-1 text-app-ink-3 text-sm">
            {invoicesQuery.isSuccess
              ? `${invoices.length} ${invoices.length === 1 ? "correo" : "correos"} en la bandeja`
              : "Facturas electrónicas que llegan al buzón de la finca"}
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            data-testid="mailbox-setup-button"
            onClick={() => setMailboxOpen(true)}
            className="inline-flex items-center gap-2 self-start rounded-2xl border border-app-border bg-app-surface px-4 py-2.5 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2"
          >
            <Mail size={16} aria-hidden="true" />
            Buzón de facturas
          </button>
        )}
      </header>

      {invoicesQuery.isLoading ? (
        <ListSkeleton />
      ) : invoicesQuery.isError ? (
        <div className="mt-12 flex flex-col items-center gap-4 text-center">
          <p className="text-app-ink-2 text-sm">No se pudieron cargar los recibidos.</p>
          <button
            type="button"
            onClick={() => void invoicesQuery.refetch()}
            className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-4">
          <ReceivedInvoicesFilters filters={filters} onChange={setFilters} />
          {invoices.length === 0 ? (
            <EmptyState
              hasFilters={hasFilters}
              onSetupMailbox={canManage ? () => setMailboxOpen(true) : null}
            />
          ) : (
            <ReceivedInvoicesList
              invoices={invoices}
              accountId={accountId}
              canDismiss={canManage}
            />
          )}
        </div>
      )}

      {canManage && accountId && role && (
        <MailboxSetupDialog
          accountId={accountId}
          role={role}
          open={mailboxOpen}
          onOpenChange={setMailboxOpen}
        />
      )}
    </>
  );
}

function ListSkeleton() {
  return (
    <div className="mt-4 space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          key={i}
          className="h-20 animate-pulse rounded-2xl border border-app-border bg-app-surface-2"
        />
      ))}
    </div>
  );
}

function EmptyState({
  hasFilters,
  onSetupMailbox,
}: {
  hasFilters: boolean;
  onSetupMailbox: (() => void) | null;
}) {
  if (hasFilters) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-app-border border-dashed bg-app-surface px-6 py-12 text-center">
        <p className="text-app-ink-2 text-sm">No hay recibidos que coincidan con los filtros.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-app-surface-2 text-app-ink-2">
        <Inbox size={20} />
      </div>
      <div>
        <h2 className="font-medium text-app-ink-1 text-xl tracking-tight">
          Todavía no llegaron facturas
        </h2>
        <p className="mt-1 max-w-sm text-app-ink-3 text-sm">
          Activá el buzón y reenviá ahí los correos de facturas — aparecen solas en esta bandeja,
          listas para asignar a lotes.
        </p>
      </div>
      {onSetupMailbox && (
        <button
          type="button"
          onClick={onSetupMailbox}
          className="inline-flex items-center gap-2 rounded-2xl bg-app-ink-1 px-4 py-2.5 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d]"
        >
          <Mail size={16} strokeWidth={2} aria-hidden="true" />
          Configurar buzón
        </button>
      )}
    </div>
  );
}
